/**
 * Collection Variant Grouping
 * Handles color swatch interactions for grouped product variants on collection pages
 */

(function() {
  'use strict';

  // Cache for fetched product data to avoid redundant requests
  const productCache = {};

  /**
   * Initialize variant grouping functionality
   */
  function init() {
    // Handle swatch clicks
    document.addEventListener('click', handleSwatchClick);

    // Collapse the swatch row to one line on mobile, with a "+N" overflow link
    initSwatchOverflow();

    // Log initialization
    console.log('[Variant Grouping] Initialized');
  }

  /**
   * Mobile swatch row overflow
   * On narrow viewports, hides color swatches that don't fit in one row and appends a
   * "+N" link to the product page. The swatch set itself never changes after an AJAX
   * color swap (only the active class does - see updateActiveSwatch), so this only
   * needs to run on load and on resize/orientation-change, not on every swatch click.
   */
  const SWATCH_OVERFLOW_QUERY = '(max-width: 768px)';

  function initSwatchOverflow() {
    const mediaQuery = window.matchMedia(SWATCH_OVERFLOW_QUERY);
    const run = function() {
      updateAllSwatchOverflow(mediaQuery.matches);
    };

    run();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', run);
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(run);
    }

    let resizeTimer;
    window.addEventListener('resize', function() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(run, 150);
    });
  }

  function updateAllSwatchOverflow(isMobile) {
    const containers = document.querySelectorAll('.grid-product__colors--grouped');
    containers.forEach(function(container) {
      updateSwatchOverflow(container, isMobile);
    });
  }

  function updateSwatchOverflow(container, isMobile) {
    const swatches = Array.prototype.slice.call(
      container.querySelectorAll('.color-swatch--grouped:not(.color-swatch--overflow-more)')
    );
    const existingMore = container.querySelector('.color-swatch--overflow-more');

    // Reset to a clean slate before re-measuring (resize can cross the breakpoint either way).
    swatches.forEach(function(swatch) {
      swatch.style.display = '';
    });
    if (existingMore) {
      existingMore.remove();
    }
    container.classList.remove('grid-product__colors--collapsed');

    if (!isMobile || swatches.length === 0) {
      return;
    }

    // Force one row, then measure the real overflow (scrollWidth vs clientWidth) rather than
    // computing it from swatch/gap pixel values. assets/color-swatches-custom.css carries
    // several unscoped `!important` size/gap overrides on these same classes, so the rendered
    // dimensions don't always match what theme.css.liquid declares - measuring what actually
    // painted is the only way this stays correct regardless of that legacy cascade.
    container.classList.add('grid-product__colors--collapsed');

    if (container.scrollWidth <= container.clientWidth + 1) {
      // Everything already fits on one row - no overflow link needed.
      container.classList.remove('grid-product__colors--collapsed');
      return;
    }

    const more = appendSwatchOverflowLink(container, swatches[0].getBoundingClientRect().width || 18);

    let hiddenCount = 0;
    let visible = swatches.slice();
    while (container.scrollWidth > container.clientWidth + 1 && visible.length > 0) {
      const last = visible.pop();
      last.style.display = 'none';
      hiddenCount++;
    }

    more.setAttribute('aria-label', '+' + hiddenCount + ' more colors');
    more.textContent = '+' + hiddenCount;
  }

  function appendSwatchOverflowLink(container, size) {
    const gridItem = container.closest('.grid-product');
    const productLink = gridItem ? gridItem.querySelector('.grid-product__link') : null;

    const more = document.createElement('a');
    more.href = productLink ? productLink.getAttribute('href') : '#';
    more.className = 'color-swatch color-swatch--small color-swatch--grouped color-swatch--overflow-more';
    more.style.width = size + 'px';
    more.style.height = size + 'px';

    // The active color can change between a resize and a click (AJAX swap updates
    // .grid-product__link without re-running this overflow calc) - read the live href
    // just before navigating so "+N" always lands on the currently selected color.
    more.addEventListener('click', function() {
      const currentLink = gridItem ? gridItem.querySelector('.grid-product__link') : null;
      if (currentLink) {
        more.href = currentLink.getAttribute('href');
      }
    });

    container.appendChild(more);
    return more;
  }

  /**
   * Handle color swatch click events
   */
  function handleSwatchClick(e) {
    const swatch = e.target.closest('.color-swatch--grouped');
    if (!swatch) return;

    e.preventDefault();
    e.stopPropagation();

    // Skip if already active
    if (swatch.classList.contains('color-swatch--active')) return;

    const variantHandle = swatch.dataset.variantHandle;
    const gridItem = swatch.closest('.grid-product--grouped');

    if (!variantHandle || !gridItem) {
      console.error('[Variant Grouping] Missing data attributes');
      return;
    }

    // Add loading state
    gridItem.classList.add('is-loading');

    // Fetch and update product
    fetchProductData(variantHandle)
      .then(data => {
        if (data) {
          updateGridItem(gridItem, data, swatch);
        }
      })
      .catch(error => {
        console.error('[Variant Grouping] Error:', error);
      })
      .finally(() => {
        gridItem.classList.remove('is-loading');
      });
  }

  /**
   * Fetch product data from Shopify API
   */
  async function fetchProductData(handle) {
    // Check cache first
    if (productCache[handle]) {
      console.log('[Variant Grouping] Using cached data for:', handle);
      return productCache[handle];
    }

    try {
      const response = await fetch(`/products/${handle}.js`);

      if (!response.ok) {
        throw new Error(`Product not found: ${handle}`);
      }

      const data = await response.json();

      // Cache the data
      productCache[handle] = data;

      console.log('[Variant Grouping] Fetched data for:', handle);
      return data;
    } catch (error) {
      console.error('[Variant Grouping] Fetch error:', error);
      return null;
    }
  }

  /**
   * Update grid item with new product data
   */
  function updateGridItem(gridItem, productData, clickedSwatch) {
    if (!productData) return;

    // Update image
    updateImage(gridItem, productData);

    // Update hover/secondary image
    updateSecondaryImage(gridItem, productData);

    // Update title
    updateTitle(gridItem, productData);

    // Update price
    updatePrice(gridItem, productData);

    // Update badges
    updateBadges(gridItem, productData);

    // Update link
    updateLink(gridItem, productData);

    // Update quick shop button
    updateQuickShop(gridItem, productData);

    // Update active swatch
    updateActiveSwatch(gridItem, clickedSwatch);

    console.log('[Variant Grouping] Updated grid item:', productData.handle);
  }

  /**
   * Update product image
   */
  function updateImage(gridItem, productData) {
    const imgWrapper = gridItem.querySelector('.grid__image-ratio, .image-wrap');
    const img = imgWrapper?.querySelector('img:not(.grid-product__secondary-image img)');

    if (img && productData.featured_image) {
      const originalSrc = img.src;
      const newSrc = getSizedImageUrl(productData.featured_image, '540x');

      if (originalSrc !== newSrc) {
        img.src = newSrc;
        img.srcset = generateSrcset(productData.featured_image);
        img.alt = productData.title;
      }
    }
  }

  /**
   * Update secondary/hover image
   */
  function updateSecondaryImage(gridItem, productData) {
    const secondaryImgWrapper = gridItem.querySelector('.grid-product__secondary-image');
    const secondaryImg = secondaryImgWrapper?.querySelector('img');

    if (secondaryImg && productData.images && productData.images.length > 1) {
      secondaryImg.src = getSizedImageUrl(productData.images[1], '540x');
      secondaryImg.srcset = generateSrcset(productData.images[1]);
    }
  }

  /**
   * Update product title
   */
  function updateTitle(gridItem, productData) {
    const title = gridItem.querySelector('.grid-product__title');
    if (title) {
      title.textContent = productData.title;
    }
  }

  /**
   * Update product price
   */
  function updatePrice(gridItem, productData) {
    const priceWrapper = gridItem.querySelector('.grid-product__price');
    if (!priceWrapper) return;

    const onSale = productData.compare_at_price && productData.compare_at_price > productData.price;
    const isUpcoming = productData.tags.includes('upcoming');
    let priceHTML = '';

    if (!isUpcoming) {
      if (onSale) {
        priceHTML += '<span class="visually-hidden">Regular price</span>';
        priceHTML += `<span class="grid-product__price--original">${formatMoney(productData.compare_at_price)}</span>`;
        // Trailing space matches the Liquid snippet output and gives the browser
        // a soft-wrap point between compare-at and current price (both nowrap spans)
        priceHTML += ' <span class="visually-hidden">Sale price</span>';
      }

      if (productData.price_varies) {
        priceHTML += `From <span class="grid-product__price--current">${formatMoney(productData.price_min)}</span>`;
      } else {
        priceHTML += `<span class="grid-product__price--current">${formatMoney(productData.price)}</span>`;
      }

      // Add savings if on sale
      if (onSale) {
        const savedAmount = productData.compare_at_price - productData.price;
        priceHTML += `<span class="grid-product__price--savings">Save ${formatMoney(savedAmount)}</span>`;
      }
    } else {
      if (productData.price_varies) {
        priceHTML += `From <span class="grid-product__price--current">${formatMoney(productData.price_min)}</span>`;
      } else {
        priceHTML += `<span class="grid-product__price--current">${formatMoney(productData.price)}</span>`;
      }
    }

    priceWrapper.innerHTML = priceHTML;
  }

  /**
   * Update product badges
   */
  function updateBadges(gridItem, productData) {
    const badge = gridItem.querySelector('.grid-product__tag');
    if (!badge) return;

    // Badge priority: UPCOMING > CUSTOM > SOLD OUT > NEW > RESTOCK > SALE
    const isUpcoming = productData.tags.includes('upcoming');
    const hasNewTag = productData.tags.some(tag => tag.toLowerCase() === 'new');
    const hasRestockTag = productData.tags.some(tag => tag.toLowerCase() === 'restock');
    const onSale = productData.compare_at_price && productData.compare_at_price > productData.price;
    const hasCustomLabel = productData.tags.some(tag => tag.startsWith('_label_'));

    // Reset badge display
    badge.style.display = '';

    if (isUpcoming) {
      badge.textContent = 'Coming Soon';
      badge.className = 'grid-product__tag grid-product__tag--upcoming';
    } else if (hasCustomLabel) {
      const customLabelTag = productData.tags.find(tag => tag.startsWith('_label_'));
      const customLabel = customLabelTag.replace('_label_', '');
      badge.textContent = customLabel;
      badge.className = 'grid-product__tag grid-product__tag--custom';
    } else if (!productData.available) {
      badge.textContent = 'Sold Out';
      badge.className = 'grid-product__tag grid-product__tag--sold-out';
    } else if (hasNewTag) {
      badge.textContent = 'NEW';
      badge.className = 'grid-product__tag grid-product__tag--new';
    } else if (hasRestockTag) {
      badge.textContent = 'BACK IN STOCK';
      badge.className = 'grid-product__tag grid-product__tag--restock';
    } else if (onSale) {
      badge.textContent = 'Sale';
      badge.className = 'grid-product__tag grid-product__tag--sale';
    } else {
      badge.style.display = 'none';
    }
  }

  /**
   * Update product link
   */
  function updateLink(gridItem, productData) {
    const link = gridItem.querySelector('.grid-product__link');
    if (!link) return;

    // Extract collection path from current URL
    const currentHref = link.href;
    const collectionMatch = currentHref.match(/(.*\/collections\/[^\/]+)/);
    const collectionPath = collectionMatch ? collectionMatch[1] : '';

    // Update link href
    if (collectionPath) {
      link.href = `${collectionPath}/products/${productData.handle}`;
    } else {
      link.href = `/products/${productData.handle}`;
    }

    // Update data attributes
    link.dataset.productUrl = link.href;
    link.dataset.productTitle = productData.title;
    link.dataset.productPrice = productData.price;
    link.dataset.productComparePrice = productData.compare_at_price || '';
    link.dataset.productAvailable = productData.available;
  }

  /**
   * Update quick shop button
   */
  function updateQuickShop(gridItem, productData) {
    const quickShopBtn = gridItem.querySelector('.quick-product__btn');
    if (!quickShopBtn) return;

    // Update modal trigger class
    quickShopBtn.className = quickShopBtn.className.replace(
      /js-modal-open-quick-modal-\d+/,
      `js-modal-open-quick-modal-${productData.id}`
    );
  }

  /**
   * Update active swatch state
   */
  function updateActiveSwatch(gridItem, clickedSwatch) {
    // Remove active class from all swatches
    gridItem.querySelectorAll('.color-swatch--grouped').forEach(swatch => {
      swatch.classList.remove('color-swatch--active');
    });

    // Add active class to clicked swatch
    clickedSwatch.classList.add('color-swatch--active');
  }

  /**
   * Get sized image URL
   */
  function getSizedImageUrl(imageUrl, size) {
    if (!imageUrl) return '';

    // Remove any existing size parameter
    const cleanUrl = imageUrl.split('?')[0];

    // Replace image size
    return cleanUrl.replace(
      /\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)$/i,
      `_${size}.$1`
    );
  }

  /**
   * Generate srcset for responsive images
   */
  function generateSrcset(imageUrl) {
    const sizes = ['360', '540', '720', '900', '1080'];
    return sizes
      .map(size => `${getSizedImageUrl(imageUrl, `${size}x`)} ${size}w`)
      .join(', ');
  }

  /**
   * Format money using Shopify's money format
   */
  function formatMoney(cents) {
    if (typeof cents === 'undefined' || cents === null) return '';

    // Basic formatting - use Shopify's theme.Currency if available
    if (window.theme && window.theme.Currency && window.theme.Currency.formatMoneyAuto) {
      return window.theme.Currency.formatMoneyAuto(cents);
    }

    // Fallback when theme.js hasn't loaded: honor the shop's money format string
    // (theme.settings.moneyFormat is set inline in theme.liquid before any asset loads).
    // Best-effort only - no thousands delimiters, unlike theme.Currency.
    const amount = (cents / 100).toFixed(2);
    const format = window.theme && window.theme.settings && window.theme.settings.moneyFormat;
    if (format) {
      return format
        .replace(/\{\{\s*amount\s*\}\}/, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toString())
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, Math.round(cents / 100).toString());
    }
    return `€${amount}`;
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
